import type { GetState } from 'types/redux'
import api from '@disco/api/server'
import {
  BILLING_PROVIDER_COMPONENTS_LOAD,
  BILLING_PROVIDER_COMPONENTS_SUCCESS,
} from '@disco/constants/ActionTypes'
import {
  DISCOVERY_SUITE,
  STORAGE,
  USER,
  WATERMARKING,
} from '@disco/constants/billing-provider-components'
import type { BillingProviderComponentsState } from '@disco/state-management/reducers/billing-provider-components'
import { getSubscription } from '@disco/state-management/selectors/subscription'
import { UNLIMITED_WATERMARKING_QUOTA } from '@disco/utils/getWatermarkingQuota'
/**
 * Available quota options for Discovery Suite auto-tagging downloads.
 * Values represent the allocated quantity for auto-tagging downloads.
 */
export const DS_QUOTAS = [0.25, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 10, 20]
export const DS_QUOTAS_PRICES = {
  0.25: { amount_in_cents: 1000 },
  0.5: { amount_in_cents: 2000 },
  1: { amount_in_cents: 4000 },
  2: { amount_in_cents: 6000 },
  3: { amount_in_cents: 7500 },
  4: { amount_in_cents: 8000 },
  5: { amount_in_cents: 9000 },
  6: { amount_in_cents: 10800 },
  7: { amount_in_cents: 12600 },
  8: { amount_in_cents: 14400 },
  10: { amount_in_cents: 15000 },
  20: { amount_in_cents: 20000 },
}

/**
 * Available quota options for watermarking functionality.
 * Values represent the allocated quantity for watermarking operations.
 */
export const WM_QUOTAS = [1, 3, UNLIMITED_WATERMARKING_QUOTA]
export const WM_QUOTAS_PRICES = {
  1: { amount_in_cents: 2900 },
  3: { amount_in_cents: 6900 },
  [UNLIMITED_WATERMARKING_QUOTA]: { amount_in_cents: 12900 },
}

/**
 * Loads billing provider components data including pricing and billing information.
 * Fetches component data, plans, and pricing for different subscription types.
 * @returns Thunk that loads components data and dispatches success/failure actions
 */
export const loadBillingProviderComponents = (): ((
  dispatch: any,
  getState: GetState,
) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    {
      const billingProviderComponents: BillingProviderComponentsState =
        getState().billingProviderComponents

      if (
        billingProviderComponents.loading ||
        billingProviderComponents.loaded
      ) {
        // TODO: implement error handling
        return
      }
    }
    const subscription = getSubscription(getState())
    dispatch({
      type: BILLING_PROVIDER_COMPONENTS_LOAD,
    })

    const chargebeePlans = await api.chargebeeListPlans()
    let addons
    if (subscription?.loaded) {
      addons = await api.chargebeeListAddons()
    } else {
      // signup flow, addons are loaded per-plan
      addons = null
    }
    dispatch({
      type: BILLING_PROVIDER_COMPONENTS_SUCCESS,
      components: {
        [STORAGE]: {
          monthly: {},
          annual: {},
        },
        [USER]: {
          monthly: {},
          annual: {},
        },
        originals: {
          [DISCOVERY_SUITE]: {},
          [STORAGE]: {},
          [USER]: {},
          [WATERMARKING]: {},
        },
        plans: {},
        chargebeePlans,
        addons,
      },
    })
    return
  }
}
